import get from 'lodash/get';
import map from 'lodash/map';
import ldSortBy from 'lodash/sortBy';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import DataTable from 'components/DataTable';
import { handleErrorResponse, parseJSON } from 'utils/general';
import MKTypography from 'components/MaterialKit/MKTypography';
import { getData, updateDatum } from 'api/general';
import { useAuth } from 'contexts/auth';
import Image from 'components/Image';
import MKButton from 'components/MaterialKit/MKButton';
import MKInput from 'components/MaterialKit/MKInput';
import { styled, Switch } from '@mui/material';
import { getConfiguration } from 'api/configurations';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getUserFedexAccounts } from 'api/fedex';

const REFRESH_DATA_INTERVAL_CONFIGURATION_ID = '01960e9f-63a5-7b2d-817a-5d6d79f3e785';
const REFRESH_SCREEN_INTERVAL_CONFIGURATION_ID = '0195fb5f-6223-723b-ae1b-f6ab36cf6f9b';

const TrackingDashboardSection = ({
  id_field,
  exit_path,
  show_title,
  show_unwanted_button,
  show_add_button,
  show_auto_next_page_button,
  show_auto_refresh_button,
  show_full_screen_button,
  next_page_path,
  auto_refresh,
  auto_next_page,
  title,
  title_font_size,
  title_color,
  unwanted_button_text,
  unwanted_button_color,
  unwanted_font_color,
  full_screen_button_text,
  full_screen_button_color,
  full_screen_font_color,
  input_track_id_label,
  add_button_text,
  add_button_color,
  add_font_color,
  auto_next_page_text,
  auto_next_page_font_color,
  auto_refresh_text,
  auto_refresh_font_color,
  switch_button_color,
  table_name,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params_1,
  extra_params_2,
  container_props,
  hk_inbound_address_configuration_id,
  jp_inbound_address_configuration_id,
  is_inbound,
  is_hk,
  is_jp,
  is_inbound_status,
  next_page_list,
  selectable,
  support_pagination,
  header_font_weight,
  body_font_weight,
  ...props
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const { auth, setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);
  const [colorRowsDataMap, setColorRowsDataMap] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formattedTableColumns, setFormattedTableColumns] = useState([]);
  const [inputTrackId, setInputTrackId] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [autoNextPage, setAutoNextPage] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshDataTime, setRefreshDataTime] = useState(null);
  const [refreshScreenTime, setRefreshScreenTime] = useState(null);
  const [timeInterval, setTimeInterval] = useState(null);
  const [pageTimeout, setPageTimeout] = useState(null);
  const [inboundAddress, setInboundAddress] = useState();
  const [inboundPackageCount, setInboundPackageCount] = useState(0);
  const [nextPagePath, setNextPagePath] = useState();
  const [filteredNextPageList, setFilteredNextPageList] = useState([]);
  const [userFedexAccounts, setUserFedexAccounts] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);
  const [refreshedTimeCount, setRefreshedTimeCount] = useState(0);

  const fetchUserFedexAccountsFromApi = useCallback(() => {
    if (!auth?.user) {
      return Promise.resolve();
    }
    return getUserFedexAccounts({ $expand: 'fedex_account', user: auth?.user?.username })
      .then(({ data }) => {
        const newUserFedexAccounts = ldSortBy(map(data, 'fedex_account'), ['sequence']);
        setUserFedexAccounts(newUserFedexAccounts);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, setAuth]);

  useEffect(() => {
    fetchUserFedexAccountsFromApi();
  }, [fetchUserFedexAccountsFromApi]);

  useEffect(() => {
    if (filteredNextPageList.length > 0) {
      let pageIndex = filteredNextPageList.indexOf(pathname);
      if (pageIndex !== -1) {
        pageIndex += 1;
        if (pageIndex >= filteredNextPageList.length) {
          pageIndex = 0;
        }
      }

      // the original code will skip the last route (other_jp) route if both HK and JP are included.
      // debug whether this is the correct behaviour

      setNextPagePath(filteredNextPageList[pageIndex]);
    }
  }, [filteredNextPageList, next_page_list, pathname, userFedexAccounts]);

  useEffect(() => {
    const isEmptyUserFedexAccounts = !userFedexAccounts || !userFedexAccounts.length;
    if (isEmptyUserFedexAccounts) {
      if (filteredNextPageList && filteredNextPageList.length > 0) {
        setFilteredNextPageList([]);
      }
    } else if (userFedexAccounts.length > 0) {
      let hasHK = false;
      let hasJP = false;
      userFedexAccounts.forEach(fedexAccount => {
        const key = fedexAccount?.key;
        if (key) {
          const lowerCaseKey = key.toLowerCase();
          if (lowerCaseKey.startsWith('hk')) {
            hasHK = true;
          } else if (lowerCaseKey.startsWith('jp')) {
            hasJP = true;
          }
        }
      });
      let newFilteredNextPageList = [];
      if (hasHK) {
        newFilteredNextPageList = newFilteredNextPageList.concat(next_page_list.slice(0, 4));
      }
      if (hasJP) {
        newFilteredNextPageList = newFilteredNextPageList.concat(next_page_list.slice(4, 4));
      }

      // the original code set the newFilteredNextPageList will skip the last route (other_jp) if both HK and JP are included:
      // setFilteredNextPageList(next_page_list.slice(0, -1));
      // debug whether this is the correct behaviour

      setFilteredNextPageList(newFilteredNextPageList);
    }
  }, [next_page_list, userFedexAccounts]);

  const escFunction = useCallback(() => {
    clearTimeout(pageTimeout);
    clearInterval(timeInterval);
    navigate(exit_path);
    window.location.reload();
  }, [exit_path, navigate, pageTimeout, timeInterval]);

  useEffect(() => {
    if (!show_full_screen_button) {
      document.addEventListener('keydown', escFunction, false);
      return () => {
        document.removeEventListener('keydown', escFunction, false);
      };
    }
  }, [escFunction, show_full_screen_button]);

  const fetchDataFromApi = useCallback((extra_params) => {
    const dataParams = {
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      ...(support_pagination ? {
        $skip: currentPage * pageSize,
        $top: pageSize,
      } : {}),
      ...extra_params,
    };
    // if (userFedexAccounts && userFedexAccounts.length > 0) {
    //   const accountKeys = map(userFedexAccounts, 'key');
    //   dataParams['account[in]'] = accountKeys.join();
    // } else {
    //   dataParams['account[null]'] = true;
    // }
    return getData(`/${table_name}`, dataParams, 'bearer');
  }, [currentPage, pageSize, sortBy, sortDirection, support_pagination, table_name, userFedexAccounts]);

  const combinedDataFromApi = useCallback(() => {
    const promises = [fetchDataFromApi(extra_params_1)];
    if (extra_params_2) {
      promises.push(fetchDataFromApi(extra_params_2));
    }
    return Promise.all(promises)
      .then((results) => {
        let totalRowsCount = 0;
        let allRowsData = [];
        const allColorDataMap = {};
        let inboundCount = 0;
        results.forEach((res) => {
          const { data, headers } = res;
          const contentRange = get(headers, 'content-range');
          const size = Number(contentRange.split('/')[1]);
          totalRowsCount += size;
          const formattedData = data.map((datum) => {
            if (datum.row_color) {
              allColorDataMap[datum[id_field]] = datum.row_color;
            }
            if (is_inbound && is_inbound_status.length > 0 && is_inbound_status.some((status) => status.toLowerCase() === datum.status.toLowerCase())) {
              // if (inboundAddress && datum.recipient_city.toLowerCase() === inboundAddress.toLowerCase()) {
              inboundCount += 1;
              // }
            }
            return { ...datum, id: datum[id_field] };
          });
          allRowsData.push(...formattedData);
        });
        setInboundPackageCount(inboundCount);
        setTotalRows(totalRowsCount);
        if (!support_pagination) {
          // if (pageSize * refreshedTimeCount > allRowsData.length) {
          //   setRefreshedTimeCount(0);
          //   setRowsData(allRowsData.slice(0, pageSize));
          // } else {

          allRowsData = allRowsData.slice(pageSize * refreshedTimeCount, pageSize * (refreshedTimeCount + 1));
          setRowsData(allRowsData);
          // }
        } else {
          setRowsData(allRowsData);
        }
        setColorRowsDataMap(allColorDataMap);
        if (allRowsData.length <= 0 && nextPagePath) {
          console.log('no data')
          clearTimeout(pageTimeout);
          clearInterval(timeInterval);
          setTimeout(() => {
            navigate(nextPagePath || filteredNextPageList[0]);
            window.location.reload();
          }, 1000);
        }
      });
  }, [extra_params_1, extra_params_2, fetchDataFromApi, filteredNextPageList, id_field, is_inbound, is_inbound_status, navigate, nextPagePath, pageSize, pageTimeout, refreshedTimeCount, support_pagination, timeInterval]);

  const fetchRefreshDataValueFromApi = useCallback(() => {
    return getConfiguration(REFRESH_DATA_INTERVAL_CONFIGURATION_ID)
      .then(({ data }) => {
        setRefreshDataTime(data?.fix_value);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  const fetchRefreshScreenValueFromApi = useCallback(() => {
    return getConfiguration(REFRESH_SCREEN_INTERVAL_CONFIGURATION_ID)
      .then(({ data }) => {
        setRefreshScreenTime(data?.fix_value);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  useEffect(() => {
    if (refreshDataTime && autoRefresh && timeInterval == null && table_name) {
      const refreshDataInterval = setInterval(() => {
        if (!support_pagination) {
          setRefreshedTimeCount((prev) => {
            return prev + 1;
          });
        }
        combinedDataFromApi();
      }, refreshDataTime);
      setTimeInterval(refreshDataInterval);
    }
  }, [autoRefresh, combinedDataFromApi, refreshedTimeCount, refreshDataTime, support_pagination, table_name, timeInterval]);

  useEffect(() => {
    if (!autoRefresh && timeInterval != null) {
      clearInterval(timeInterval);
      setTimeInterval(null);
    }
  }, [autoRefresh, refreshDataTime, timeInterval]);

  useEffect(() => {
    if (autoRefresh) {
      fetchRefreshDataValueFromApi();
    }
    if (autoNextPage) {
      fetchRefreshScreenValueFromApi();
    }
  }, [autoRefresh, autoNextPage, fetchRefreshDataValueFromApi, fetchRefreshScreenValueFromApi]);

  useEffect(() => {
    return () => {
      clearInterval(timeInterval);
      // clearTimeout(pageTimeout);
    };
  }, [timeInterval, pageTimeout]);

  useEffect(() => {
    // if (refreshScreenTime && autoNextPage && table_name && nextPagePath && filteredNextPageList.length > 0) {
    //   const timeout = setTimeout(() => {
    //     navigate(nextPagePath || filteredNextPageList[0]);
    //   }, refreshScreenTime);
    //   setPageTimeout(timeout);
    //   return () => {
    //     clearTimeout(timeout);
    //   };
    // }
  }, [autoNextPage, filteredNextPageList, navigate, nextPagePath, refreshScreenTime, table_name]);

  useEffect(() => {
    if (auto_refresh) {
      setAutoRefresh(auto_refresh);
    }
    if (auto_next_page) {
      setAutoNextPage(auto_next_page);
    }
  }, [auto_next_page, auto_refresh]);

  useEffect(() => {
    if (page_options?.length > 0) {
      setPageSize(page_options[0]);
    }
  }, [page_options]);

  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMobileChange = (event) => {
      setIsMobile(event.matches);
    };
    mobileMediaQuery.addEventListener('change', handleMobileChange);
    setIsMobile(mobileMediaQuery.matches);
    return () => {
      mobileMediaQuery.removeEventListener('change', handleMobileChange);
    };
  }, []);

  useEffect(() => {
    if (table_name) {
      combinedDataFromApi();
    }
  }, [combinedDataFromApi, table_name]);

  const onClickSort = useCallback((headerName) => {
    setSortBy(headerName);
    setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  const handleInputTrackId = useCallback((e) => {
    setInputTrackId(e.target.value)
  }, []);

  const handleAddTrackingId = useCallback(() => {
    return updateDatum(`/${table_name}`, inputTrackId, { is_unwanted: false }, 'bearer')
      .then(() => {
        alert(`Added Tracking Number: ${inputTrackId}`);
        window.location.reload();
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [inputTrackId, setAuth, table_name]);

  const fetchInboundAddressFromApi = useCallback((configuration_id) => {
    return getConfiguration(configuration_id)
      .then(({ data }) => {
        setInboundAddress(data.fix_value);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth]);

  useEffect(() => {
    if (is_inbound) {
      if (is_hk && hk_inbound_address_configuration_id) {
        fetchInboundAddressFromApi(hk_inbound_address_configuration_id);
      }
      if (is_jp && jp_inbound_address_configuration_id) {
        fetchInboundAddressFromApi(jp_inbound_address_configuration_id);
      }
    }
  }, [fetchInboundAddressFromApi, hk_inbound_address_configuration_id, is_hk, is_inbound, is_jp, jp_inbound_address_configuration_id]);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const columnName in value_mapping_config) {
      // eslint-disable-next-line no-loop-func
      (columns_config || []).forEach((column) => {
        if (column.field === columnName) {
          // eslint-disable-next-line no-param-reassign
          column.renderContent = (activity) => {
            const type = value_mapping_config[columnName]?.type;
            let fedexStatusImg;
            if (type === 'fedex_status' && activity.status) {
              // const status = value_mapping_config[columnName];
              const fedexStatus = Object.keys(value_mapping_config[columnName]).find((key) => activity.status.toLowerCase().includes(key.toLowerCase()));
              fedexStatusImg = value_mapping_config[columnName][fedexStatus];
            }
            if (activity[columnName] || activity[columnName] !== null) {
              return (
                <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
                  {type && type.includes('image') && (
                    <Image
                      width={value_mapping_config[columnName]?.image_width}
                      src={value_mapping_config[columnName][`${activity[columnName]}_image`] || value_mapping_config[columnName]?.image}
                      style={{ marginRight: '0.25rem' }}
                      {...(type.includes('url') && {
                        onClick: () => window.open(activity[columnName], '_blank').focus(),
                        style: { cursor: 'pointer' },
                      })}
                    />
                  )}
                  {type && type.includes('string') && (
                    <MKTypography
                      fontSize="1rem"
                      color={activity.row_color === 'red' ? 'white' : (value_mapping_config[columnName][`${activity[columnName]}_color`] || value_mapping_config[columnName]?.color)}
                      fontWeight={value_mapping_config[columnName]?.font_weight}
                    >
                      {value_mapping_config[columnName][activity[columnName]]}
                    </MKTypography>
                  )}
                  {type && type.includes('datetime') && activity[columnName] && (
                    <MKTypography
                      fontSize="1rem"
                      color={activity.row_color === 'red' ? 'white' : (value_mapping_config[columnName][`${activity[columnName]}_color`] || value_mapping_config[columnName]?.color)}
                      fontWeight={value_mapping_config[columnName]?.font_weight}
                    >
                      {moment(activity[columnName]).format(value_mapping_config[columnName]?.format)}
                    </MKTypography>
                  )}
                  {type && type.includes('fedex_status') && (
                    <MKBox display="flex" alignItems="center" width="100%">
                      <MKBox display="flex" flex={1} justifyContent="flex-start" alignItems="center">
                        {fedexStatusImg && (
                          <Image
                            width={value_mapping_config[columnName]?.image_width}
                            style={{ marginRight: '0.25rem' }}
                            src={fedexStatusImg}
                          />
                        )}
                      </MKBox>
                      <MKBox display="flex" flex={3} justifyContent="flex-start" alignItems="center">
                        <MKTypography
                          fontSize="1rem"
                          fontWeight={value_mapping_config[columnName]?.font_weight}
                          color={activity.row_color === 'red' ? 'white' : (value_mapping_config[columnName][`${activity[columnName]}_color`] || value_mapping_config[columnName]?.color)}
                          // onClick={() => window.open(activity[columnName], '_blank').focus()}
                          // style={{ cursor: 'pointer' }}
                        >
                          {value_mapping_config[columnName][activity[columnName]] || activity[columnName]}
                        </MKTypography>
                      </MKBox>
                    </MKBox>
                  )}
                </MKBox>
              );
            }
            return ' ';
          };
        }
        if (column.field === 'last_location') {
          // eslint-disable-next-line no-param-reassign
          column.renderContent = (activity) => {
            const textRows = activity?.last_location.split(';') || [];
            return (
              <MKBox>
                {textRows.map((text) => (
                  <MKTypography
                    fontSize="1rem"
                    color={activity.row_color === 'red' ? 'white' : 'black'}
                    fontWeight="bold"
                  >
                    {text}
                  </MKTypography>
                ))}
              </MKBox>
            )
          }
        }
      });
    }
    setFormattedTableColumns(columns_config);
  }, [columns_config, value_mapping_config]);

  const CustomizedSwitch = styled((switchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...switchProps} />
  ))(() => ({
    'width': 60,
    'height': 30,
    'padding': 0,
    '& .MuiSwitch-switchBase': {
      'padding': 0,
      'transitionDuration': '300ms',
      '&.Mui-checked': {
        'transform': 'translateX(28px)',
        'color': '#fff',
        'borderColor': 'white !important',
        '& + .MuiSwitch-track': {
          backgroundColor: `${switch_button_color} !important`,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
        '& .MuiSwitch-thumb': {
          borderColor: `${switch_button_color} !important`,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 1,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 30,
      height: 30,
    },
    '& .MuiSwitch-track': {
      borderRadius: 30,
      height: 30,
      width: 60,
      backgroundColor: 'lightgray',
      opacity: 1,
    },
  }));

  const onSelectRow = useCallback((id, checked) => {
    if (checked) {
      setSelectedPackageIds([...selectedPackageIds, id]);
    } else {
      setSelectedPackageIds(selectedPackageIds.filter((selectedId) => id !== selectedId));
    }
  }, [selectedPackageIds]);

  const handleUnwantedPackage = useCallback(() => {
    return Promise.all(selectedPackageIds.map((id) => updateDatum(`/${table_name}`, id, { is_unwanted: true }, 'bearer')))
      .then((promises) => {
        const updatedTrackId = [];
        promises.forEach((promise) => {
          if (promise?.data) {
            updatedTrackId.push(promise.data.tracking_number);
          }
        });
        alert(`Removed Tracking Number: ${updatedTrackId}`);
        window.location.reload();
      });
  }, [selectedPackageIds, table_name]);

  const onClickRow = (id) => {
    window.open(id, '_blank').focus();
  };

  return (
    <MKBox
      sx={{
        ...parseJSON(container_props, {}),
      }}
      {...props}
    >
      <MKBox mb={2} display="flex" alignItems="center">
        {show_title && (
          <MKTypography mr="1rem" color={title_color} fontSize={title_font_size}>
            {title}
          </MKTypography>
        )}
        {unwanted_button_text && show_unwanted_button && (
          <MKButton color={unwanted_button_color || 'red'} sx={{ padding: '5px 15px', borderRadius: 0, marginRight: '1rem' }} onClick={handleUnwantedPackage}>
            <MKTypography fontSize={title_font_size} color={unwanted_font_color || 'white'}>
              {unwanted_button_text}
            </MKTypography>
          </MKButton>
        )}
        {input_track_id_label && add_button_text && show_add_button && (
          <MKBox mr="1rem">
            <MKInput
              name="input_track_id"
              label={input_track_id_label}
              value={inputTrackId}
              onChange={(e) => handleInputTrackId(e)}
            />
            <MKButton color={add_button_color || 'yellow'} sx={{ padding: '5px 15px', borderRadius: 0, marginRight: '1rem' }} onClick={handleAddTrackingId}>
              <MKTypography fontSize={title_font_size} color={add_font_color || 'black'}>
                {add_button_text}
              </MKTypography>
            </MKButton>
          </MKBox>
        )}
        {auto_next_page_text && show_auto_next_page_button && (
          <MKBox mr="1rem" display="flex" alignItems="center">
            <MKTypography fontSize={title_font_size} color={auto_next_page_font_color || 'black'}>
              {auto_next_page_text}
            </MKTypography>
            <CustomizedSwitch sx={{ m: 1 }} checked={autoNextPage} onChange={() => setAutoNextPage(!autoNextPage)} />
          </MKBox>
        )}
        {auto_refresh_text && show_auto_refresh_button && (
          <MKBox mr="1rem" display="flex" alignItems="center">
            <MKTypography fontSize={title_font_size} color={auto_refresh_font_color || 'black'}>
              {auto_refresh_text}
            </MKTypography>
            <CustomizedSwitch sx={{ m: 1 }} checked={autoRefresh} onChange={() => setAutoRefresh(!autoRefresh)} />
          </MKBox>
        )}
        {full_screen_button_text && show_full_screen_button && (
          <MKButton mr="1rem" color={full_screen_button_color || 'yellow'} sx={{ padding: '5px 15px', borderRadius: 0, marginRight: '1rem' }} onClick={() => navigate(nextPagePath || filteredNextPageList[0])}>
            <MKTypography fontSize={title_font_size} color={full_screen_font_color || 'black'}>
              {full_screen_button_text}
            </MKTypography>
          </MKButton>
        )}
      </MKBox>
      {is_inbound > 0 && (
        <MKTypography marginY="1rem">
          {`Package Expected to be delivered today: ${inboundPackageCount}`}
        </MKTypography>
      )}
      <DataTable
        data={rowsData}
        colorDataMap={colorRowsDataMap}
        columns={formattedTableColumns || []}
        headerBackgroundColor={header_background_color}
        headerFontColor={header_font_color}
        oddRowBackgroundColor={odd_row_background_color}
        oddRowFontColor={odd_row_font_color}
        evenRowBackgroundColor={even_row_background_color}
        evenRowFontColor={even_row_font_color}
        idField={id_field}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={onClickSort}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={page_options}
        totalCount={totalRows}
        onRowsPerPageChange={setPageSize}
        paginationDisabled={!support_pagination}
        selectable={selectable}
        onSelectRow={onSelectRow}
        headerFontWeight={header_font_weight}
        bodyFontWeight={body_font_weight}
        rowFontSize="1rem"
        // onClickRow={selectable && onClickRow}
      />
    </MKBox>
  );
};

TrackingDashboardSection.propTypes = {
  id_field: PropTypes.string,
  exit_path: PropTypes.string,
  show_title: PropTypes.bool,
  show_unwanted_button: PropTypes.bool,
  show_add_button: PropTypes.bool,
  show_auto_next_page_button: PropTypes.bool,
  show_auto_refresh_button: PropTypes.bool,
  show_full_screen_button: PropTypes.bool,
  next_page_path: PropTypes.string,
  auto_refresh: PropTypes.bool,
  auto_next_page: PropTypes.bool,
  title: PropTypes.string,
  title_font_size: PropTypes.string,
  title_color: PropTypes.string,
  unwanted_button_text: PropTypes.string,
  unwanted_button_color: PropTypes.string,
  unwanted_font_color: PropTypes.string,
  full_screen_button_text: PropTypes.string,
  full_screen_button_color: PropTypes.string,
  full_screen_font_color: PropTypes.string,
  input_track_id_label: PropTypes.string,
  add_button_text: PropTypes.string,
  add_button_color: PropTypes.string,
  add_font_color: PropTypes.string,
  auto_next_page_text: PropTypes.string,
  auto_next_page_font_color: PropTypes.string,
  auto_refresh_text: PropTypes.string,
  auto_refresh_font_color: PropTypes.string,
  switch_button_color: PropTypes.string,
  table_name: PropTypes.string,
  columns_config: PropTypes.array,
  column_mapping_config: PropTypes.array,
  value_mapping_config: PropTypes.object,
  odd_row_background_color: PropTypes.string,
  even_row_background_color: PropTypes.string,
  odd_row_font_color: PropTypes.string,
  even_row_font_color: PropTypes.string,
  header_background_color: PropTypes.string,
  header_font_color: PropTypes.string,
  page_options: PropTypes.array,
  extra_params_1: PropTypes.object,
  extra_params_2: PropTypes.object,
  container_props: PropTypes.string,
  hk_inbound_address_configuration_id: PropTypes.string,
  jp_inbound_address_configuration_id: PropTypes.string,
  is_inbound: PropTypes.bool,
  is_hk: PropTypes.bool,
  is_jp: PropTypes.bool,
  is_inbound_status: PropTypes.array,
  next_page_list: PropTypes.array,
  selectable: PropTypes.bool,
  support_pagination: PropTypes.bool,
  header_font_weight: PropTypes.string,
  body_font_weight: PropTypes.string,
};

TrackingDashboardSection.defaultProps = {
  id_field: '',
  exit_path: '',
  show_title: false,
  show_unwanted_button: false,
  show_add_button: false,
  show_auto_next_page_button: false,
  show_auto_refresh_button: false,
  show_full_screen_button: false,
  next_page_path: '',
  auto_refresh: false,
  auto_next_page: false,
  title: '',
  title_font_size: '',
  title_color: '',
  unwanted_button_text: '',
  unwanted_button_color: '',
  unwanted_font_color: '',
  full_screen_button_color: '',
  full_screen_font_color: '',
  full_screen_button_text: '',
  input_track_id_label: '',
  add_button_text: '',
  add_button_color: '',
  add_font_color: '',
  auto_next_page_text: '',
  auto_next_page_font_color: '',
  auto_refresh_text: '',
  auto_refresh_font_color: '',
  switch_button_color: '',
  table_name: '',
  columns_config: [],
  column_mapping_config: [],
  value_mapping_config: {},
  odd_row_background_color: '',
  even_row_background_color: '',
  odd_row_font_color: '',
  even_row_font_color: '',
  header_background_color: '',
  header_font_color: '',
  page_options: [],
  extra_params_1: {},
  extra_params_2: {},
  container_props: '',
  hk_inbound_address_configuration_id: '',
  jp_inbound_address_configuration_id: '',
  is_inbound: false,
  is_hk: false,
  is_jp: false,
  is_inbound_status: [],
  next_page_list: [],
  selectable: false,
  support_pagination: false,
  header_font_weight: '',
  body_font_weight: '',
};

export default TrackingDashboardSection;
